/**
 * ZIYA motion layer: reveals, the opening hero arch, parallax, the story
 * scene, marquee, header, shop bar and swipe-to-close sheets. Everything that
 * follows the scroll position runs in one requestAnimationFrame pass and reads
 * only values measured up front. With .js-motion absent (reduced motion or
 * "Animaties aan" off) elements keep their final state and only the working
 * parts run: header, story steps and shop bar.
 */
(function () {
  'use strict';
  window.ZIYA_MOTION = true;
  var root = document.documentElement;
  var ZIYA = (window.ZIYA = window.ZIYA || {});
  var motion = root.classList.contains('js-motion');
  var lite = root.classList.contains('motion-lite');
  var fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  function wide() { return window.innerWidth >= 990; }
  function all(selector, scope) { return Array.prototype.slice.call((scope || document).querySelectorAll(selector)); }

  /* one screen-reader voice for the whole site */
  var live = document.querySelector('[data-ziya-live]');
  ZIYA.say = function (text) { if (live) { live.textContent = ''; live.textContent = text; } };
  ZIYA.liveText = function (name) { return live ? live.getAttribute('data-' + name) : ''; };

  /* drawers and dialogs: nothing behind them can be reached */
  ZIYA.inertBehind = function (on) {
    all('main, .site-header, .site-footer').forEach(function (el) { el.inert = on; });
  };

  /* ------------------------------- reveals -------------------------------- */
  // Words of one visual line share a delay, measured once after the fonts.
  function groupLines(el) {
    var top = null, line = -1;
    all('.zl', el).forEach(function (word) {
      if (word.offsetTop !== top) { top = word.offsetTop; line += 1; }
      word.firstChild.style.setProperty('--i', line);
    });
  }

  var seen = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      var tall = entry.intersectionRect.height > window.innerHeight * 0.4;
      if (!entry.isIntersecting || (entry.intersectionRatio < 0.15 && !tall)) return;
      entry.target.classList.add('is-in');
      seen.unobserve(entry.target);
    });
  }, { threshold: [0, 0.15], rootMargin: '0px 0px -10% 0px' }) : null;

  ZIYA.reveal = function (scope) {
    var items = all('[data-reveal]', scope);
    if (!motion || !seen) { items.forEach(function (el) { el.classList.add('is-in'); }); return; }
    var fonts = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
    items.forEach(function (el) {
      // cards side by side arrive one after the other, at most six apart
      var index = Array.prototype.indexOf.call(el.parentNode.children, el);
      if (index > 0 && !el.style.getPropertyValue('--d')) el.style.setProperty('--d', Math.min(index, 6) * 80 + 'ms');
      seen.observe(el);
      if (el.getAttribute('data-reveal') === 'lines') fonts.then(function () { groupLines(el); });
    });
  };
  ZIYA.reveal(document);
  document.addEventListener('shopify:section:load', function (e) { ZIYA.reveal(e.target); });

  /* ------------------------------ scroll pass ----------------------------- */
  var tasks = [], measures = [], queued = false;
  function frame() {
    queued = false;
    var y = window.scrollY;
    tasks.forEach(function (task) { task(y); });
  }
  function request() { if (!queued) { queued = true; window.requestAnimationFrame(frame); } }
  function measure() { measures.forEach(function (fn) { fn(); }); request(); }
  window.addEventListener('scroll', request, { passive: true });
  window.addEventListener('resize', measure);
  window.addEventListener('load', measure);

  /* header: cream once scrolled, away going down, back going up */
  var header = document.querySelector('[data-site-header]');
  if (header) {
    var onHero = header.getAttribute('data-on-hero') === 'true', lastY = window.scrollY;
    tasks.push(function (y) {
      header.classList.toggle('is-scrolled', y > 24);
      header.classList.toggle('on-hero', onHero && y <= 24);
      if (Math.abs(y - lastY) < 6) return;
      header.classList.toggle('is-away', y > lastY && y > 240 && !header.contains(document.activeElement));
      lastY = y;
    });
  }

  /* hero: the arch opens over the first part of the scroll */
  var hero = document.querySelector('[data-hero-arch]');
  var heroRange = 1, heroP = -1;
  if (hero) {
    var heroSticky = hero.querySelector('[data-hero-sticky]');
    measures.push(function () {
      heroRange = Math.max(1, hero.offsetHeight - heroSticky.offsetHeight + hero.offsetTop);
    });
    tasks.push(function (y) { document.body.classList.toggle('in-hero', y < heroRange); });
    if (motion && !lite) {
      tasks.push(function (y) {
        if (wide()) return; // desktop keeps the arch standing; only phones open it
        var p = Math.min(1, Math.max(0, y / heroRange));
        if (p === heroP) return;
        heroP = p;
        hero.style.setProperty('--p', p.toFixed(3));
        hero.style.setProperty('--y', y + 'px');
        hero.classList.toggle('is-opening', p > 0 && p < 1);
      });
    }
  }

  /* parallax on mood photos: desktop with a mouse only */
  if (motion && !lite && fine) {
    all('[data-parallax]').forEach(function (el) {
      var top = 0, height = 0, vh = 0;
      measures.push(function () {
        var rect = el.getBoundingClientRect();
        top = rect.top + window.scrollY;
        height = rect.height;
        vh = window.innerHeight;
      });
      var strength = Number(el.getAttribute('data-parallax')) || 8;
      tasks.push(function (y) {
        if (!wide() || y + vh < top || y > top + height) return;
        var progress = (y + vh / 2 - (top + height / 2)) / (vh + height);
        el.style.transform = 'translate3d(0,' + (progress * height * strength / 100).toFixed(1) + 'px,0) scale(1.03)';
      });
    });
  }

  /* chat button stops above the footer on desktop, so it never sits on a form */
  var bubble = document.querySelector('[data-chatbot-open]');
  var footer = document.querySelector('.site-footer');
  if (bubble && footer) {
    var footerTop = 0, screenH = 0;
    measures.push(function () {
      footerTop = footer.getBoundingClientRect().top + window.scrollY;
      screenH = window.innerHeight;
    });
    tasks.push(function (y) {
      root.style.setProperty('--chat-foot', wide() ? Math.min(screenH - 120, Math.max(0, y + screenH - footerTop)) + 'px' : '0px');
    });
  }

  /* homepage shop bar: after the hero, gone again at the closing block */
  var bar = document.querySelector('[data-shopbar]');
  if (bar) {
    var closing = document.querySelector('#final-cta');
    var atEnd = false;
    if (closing && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) { atEnd = entries[0].isIntersecting || entries[0].boundingClientRect.top < 0; request(); }).observe(closing);
    }
    tasks.push(function (y) {
      var show = y > heroRange + 60 && !atEnd;
      if (show === bar.classList.contains('is-shown')) return;
      bar.classList.toggle('is-shown', show);
      bar.inert = !show;
      document.body.classList.toggle('has-shopbar', show);
    });
  }

  /* -------------------------------- scenes -------------------------------- */
  // story: the statement crossing the reading line lights its spot on the photo
  all('[data-story]').forEach(function (story) {
    var steps = all('[data-story-step]', story);
    var spots = all('[data-story-spot]', story);
    function activate(index) {
      steps.forEach(function (s, i) { s.classList.toggle('is-active', i === index); });
      spots.forEach(function (s, i) { s.classList.toggle('is-active', i === index); });
    }
    if (!('IntersectionObserver' in window) || !steps.length) return;
    story.classList.add('is-live');
    activate(0);
    var counter = story.querySelector('[data-story-count]');
    var storyTop = 0, storyRun = 1, current = 0;
    measures.push(function () {
      var rect = story.getBoundingClientRect();
      storyTop = rect.top + window.scrollY;
      storyRun = Math.max(1, rect.height - window.innerHeight);
    });
    tasks.push(function (y) {
      if (!wide()) return;
      var index = Math.min(steps.length - 1, Math.max(0, Math.floor(((y - storyTop) / storyRun) * steps.length)));
      if (index === current) return;
      current = index;
      activate(index);
      if (counter) counter.textContent = '0' + (index + 1);
    });
    var phone = !wide();
    var observer = new IntersectionObserver(function (entries) {
      if (wide()) return;
      entries.forEach(function (entry) { if (entry.isIntersecting) activate(steps.indexOf(entry.target)); });
    }, { rootMargin: phone ? '-68% 0px -24% 0px' : '-45% 0px -45% 0px' });
    steps.forEach(function (s) { observer.observe(s); });
  });

  // marquee: stops by hand, on touch, and whenever it is off-screen
  all('[data-marquee]').forEach(function (marquee) {
    var toggle = marquee.querySelector('[data-marquee-toggle]');
    var byHand = false, offscreen = false, touching = false;
    function sync() { marquee.classList.toggle('is-paused', byHand || offscreen || touching || document.hidden); }
    if (toggle) {
      toggle.addEventListener('click', function () {
        byHand = !byHand;
        toggle.setAttribute('aria-pressed', String(byHand));
        toggle.setAttribute('aria-label', toggle.getAttribute(byHand ? 'data-play' : 'data-pause'));
        sync();
      });
    }
    marquee.addEventListener('touchstart', function () { touching = true; sync(); }, { passive: true });
    marquee.addEventListener('touchend', function () { touching = false; sync(); }, { passive: true });
    document.addEventListener('visibilitychange', sync);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) { offscreen = !entries[0].isIntersecting; sync(); }).observe(marquee);
    }
  });

  // the footer signature rises once 30% of it is in view
  var mark = document.querySelector('[data-wordmark]');
  if (mark && motion && 'IntersectionObserver' in window) {
    var markSeen = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) { mark.classList.add('is-in'); markSeen.disconnect(); }
    }, { threshold: 0.3 });
    markSeen.observe(mark);
  } else if (mark) {
    mark.classList.add('is-in');
  }

  /* ---------------------------- small rewards ----------------------------- */
  // magnetic buttons, at most 6px
  if (motion && fine) {
    document.addEventListener('pointermove', function (e) {
      var btn = e.target.closest && e.target.closest('[data-magnetic]');
      if (!btn) return;
      var r = btn.getBoundingClientRect();
      btn.style.translate = ((e.clientX - r.left) / r.width - 0.5) * 12 + 'px ' + ((e.clientY - r.top) / r.height - 0.5) * 12 + 'px';
    }, { passive: true });
    document.addEventListener('pointerout', function (e) {
      var btn = e.target.closest && e.target.closest('[data-magnetic]');
      if (btn && !btn.contains(e.relatedTarget)) btn.style.translate = '';
    });
  }

  // bottom sheets: pull the top edge down to close
  var pull = null;
  document.addEventListener('touchstart', function (e) {
    var grab = e.target.closest && e.target.closest('[data-sheet-grab]');
    if (!grab) return;
    pull = { sheet: grab.closest('[data-sheet]'), from: e.touches[0].clientY, by: 0 };
  }, { passive: true });
  document.addEventListener('touchmove', function (e) {
    if (!pull) return;
    pull.by = Math.max(0, e.touches[0].clientY - pull.from);
    pull.sheet.style.transition = 'none';
    pull.sheet.style.transform = 'translateY(' + pull.by + 'px)';
  }, { passive: true });
  document.addEventListener('touchend', function () {
    if (!pull) return;
    pull.sheet.style.transition = '';
    pull.sheet.style.transform = '';
    if (pull.by > 80) {
      var close = pull.sheet.querySelector('[data-sheet-close]');
      if (close) close.click();
    }
    pull = null;
  });

  // the cookie bar never covers the hero button
  document.addEventListener('ziya:popup-change', function (e) {
    window.requestAnimationFrame(function () {
      var banner = e.detail.open === 'cookie' ? document.querySelector('[data-cookie-banner]') : null;
      root.style.setProperty('--cookie-h', banner ? banner.offsetHeight + 'px' : '0px');
    });
  });

  // optional smooth scrolling: desktop with a mouse, never on touch
  var script = document.currentScript;
  var lenisUrl = script && script.getAttribute('data-lenis');
  if (lenisUrl && fine && motion && wide()) {
    var loader = document.createElement('script');
    loader.src = lenisUrl;
    loader.onload = function () {
      if (!window.Lenis) return;
      var smooth = new window.Lenis();
      (function raf(time) { smooth.raf(time); window.requestAnimationFrame(raf); })(0);
    };
    document.head.appendChild(loader);
  }

  measure();
  frame();
})();
